import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { useDebounceValue } from "usehooks-ts";
import { Badge } from "@/components/ui/badge";
import { statusToVariant } from "@/components/ui/badge/statusToVariant";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useLocale } from "@/hooks/useLocale";
import { useGetChatShortcutItemsQuery } from "@/services/(my-space)/api.chat";
import type {
  ChatShortcutEntityType,
  IChatShortcutItem,
} from "@/types/(my-space)/chat.interface";
import { getErrorMessage } from "@/utils/error.utils";
import { CardTile } from "./ChatCard";
import {
  CHAT_SHORTCUT_KINDS,
  chatShortcutKindFor,
  type IChatShortcutKind,
} from "../_lib/chat-shortcuts";
import { shortcutItemStatus, shortcutSubtitle } from "../_lib/shortcut-format";

/**
 * "My items": pick a kind, then one of my items of that kind. With `only`,
 * straight to that kind's list (no way back to the others).
 */
export const ShortcutItemsPanel = ({
  onPick,
  only,
}: {
  onPick: (item: IChatShortcutItem) => void;
  only?: ChatShortcutEntityType;
}) => {
  const { t } = useTranslation("chat");
  const [kind, setKind] = useState<IChatShortcutKind | null>(null);

  const fixed = chatShortcutKindFor(only);
  if (fixed) return <ItemList kind={fixed} onPick={onPick} />;

  if (!kind) {
    return (
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {CHAT_SHORTCUT_KINDS.map((k) => (
          <button
            key={k.type}
            type="button"
            onClick={() => setKind(k)}
            className="flex flex-col items-center gap-2 rounded-md border border-border p-3 text-center text-sm text-foreground hover:border-primary hover:bg-primary/5"
          >
            <CardTile tone={k.tone} isMine={false}>
              <k.icon />
            </CardTile>
            {k.label(t)}
          </button>
        ))}
      </div>
    );
  }
  return <ItemList kind={kind} onBack={() => setKind(null)} onPick={onPick} />;
};

const ItemList = ({
  kind,
  onBack,
  onPick,
}: {
  kind: IChatShortcutKind;
  onBack?: () => void;
  onPick: (item: IChatShortcutItem) => void;
}) => {
  const { t } = useTranslation("chat");
  const locale = useLocale();
  const [search, setSearch] = useState("");
  const [debounced] = useDebounceValue(search, 300);
  const query = useGetChatShortcutItemsQuery({ type: kind.type, search: debounced });
  const items = query.data ?? [];

  return (
    <div className="flex min-h-0 flex-col gap-3">
      {onBack ? (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onBack}
            aria-label={t("shortcut.back_to_kinds")}
          >
            <ChevronLeft className="rtl-flip" />
          </Button>
          <span className="text-sm font-semibold text-foreground">{kind.label(t)}</span>
        </div>
      ) : null}
      <div className="relative shrink-0">
        <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("shortcut.search_items", { kind: kind.label(t) })}
          aria-label={t("shortcut.search_items", { kind: kind.label(t) })}
          className="h-10 ps-9"
          autoFocus
        />
      </div>
      <div className="max-h-[min(22rem,55vh)] min-h-40 overflow-y-auto" aria-busy={query.isFetching}>
        {query.isLoading ? (
          <div className="flex flex-col gap-2 py-1">
            {Array.from({ length: 5 }, (_, i) => (
              <div key={i} className="flex items-center gap-3 px-2 py-1.5">
                <Skeleton className="size-10 rounded-[11px]" />
                <div className="flex flex-1 flex-col gap-1.5">
                  <Skeleton className="h-3.5 w-1/2" />
                  <Skeleton className="h-3 w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : query.isError ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <p className="text-sm text-muted-foreground">
              {getErrorMessage(query.error, t("shortcut.items_error"))}
            </p>
            <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
              {t("shortcut.retry")}
            </Button>
          </div>
        ) : items.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {debounced.trim()
              ? t("shortcut.items_no_match", { search: debounced.trim() })
              : t("shortcut.items_empty", { kind: kind.label(t) })}
          </p>
        ) : (
          <ul className="flex flex-col">
            {items.map((item) => {
              const subtitle = shortcutSubtitle(item, locale);
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => onPick(item)}
                    className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-start hover:bg-muted"
                  >
                    <CardTile tone={kind.tone} isMine={false}>
                      <kind.icon />
                    </CardTile>
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="truncate text-sm font-medium text-foreground">
                        {item.title || kind.label(t)}
                      </span>
                      {subtitle ? (
                        <span className="truncate text-xs text-muted-foreground">{subtitle}</span>
                      ) : null}
                    </span>
                    {item.status ? (
                      <Badge variant={statusToVariant(item.status)} className="shrink-0">
                        {shortcutItemStatus(t, item)}
                      </Badge>
                    ) : null}
                    <ChevronRight className="rtl-flip size-4 shrink-0 text-muted-foreground" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};
